import { useState, useEffect, useCallback } from "react";
import { isApiConnected } from "@/lib/apiClient";
import { listCourseCategoryEntries, type CourseCategoryEntry } from "@/services/courseService";

// Card data model shared by the /courses listing and the home page's
// "Choose Your Adventure" section, sourced entirely from templateID 2
// ("Courselist" — id + name only). Every other field has no backend source
// here and is filled with a safe, explicit default rather than left
// undefined.
export interface CourseCategoryCard {
  id: string;
  slug: string;
  name: string;
  category: string;
  difficulty: string;
  duration: string;
  price: number;
  seats: number;
  image: string;
  shortDescription: string;
  location: string;
}

const PLACEHOLDER_IMAGE = "/images/courses/mountaineering-hero.jpg";

// Maps the live category name to the matching local course photo — keys are
// matched case-insensitively against known name variants seen from the
// backend (see docs/API_MIGRATION_MAPPING.md for confirmed live category
// names: Mountaineering, Mountain Terrain Biking, SCUBA Diving,
// White Water Rafting, Paragliding, Paramotor).
//
// These point at the "-hero" 640px re-encodes, not the full 1400px+
// originals — both places this card image renders (this section's own card
// grid, and /courses' matching card grid) display it at well under 640px, so
// the originals were pure waste (confirmed via PageSpeed Insights' "Improve
// image delivery" audit, 2026-09-29 — this was flagged again after an
// earlier pass only fixed the home page's separate hero photo mosaic, which
// uses a different image role). The full-resolution originals are still
// used as-is wherever a bigger display size is actually needed (e.g. course
// detail pages).
const CATEGORY_IMAGES: Record<string, string> = {
  "mountaineering": "/images/courses/mountaineering-hero.jpg",
  "mountain terrain biking": "/images/courses/mtb-hero.jpg",
  "mtb": "/images/courses/mtb-hero.jpg",
  "sport climbing": "/images/courses/sports-climbing-hero.jpg",
  "sports climbing": "/images/courses/sports-climbing-hero.jpg",
  "paragliding": "/images/courses/paragliding-hero.jpg",
  "paramotor": "/images/courses/paramotor-hero.jpg",
  "paramotor trike": "/images/courses/paramotor-trike-hero.jpg",
  "scuba diving": "/images/courses/scuba-hero.jpg",
  "scuba": "/images/courses/scuba-hero.jpg",
  "white water rafting": "/images/courses/rafting-hero.jpg",
  "rafting": "/images/courses/rafting-hero.jpg",
  "white water kayaking": "/images/courses/kayaking-hero.jpg",
  "kayaking": "/images/courses/kayaking-hero.jpg",
};

export function imageForCategory(name: string): string {
  return CATEGORY_IMAGES[name.trim().toLowerCase()] || PLACEHOLDER_IMAGE;
}

export function slugifyCategory(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function toCard(entry: CourseCategoryEntry): CourseCategoryCard {
  return {
    id: entry.id,
    slug: slugifyCategory(entry.name),
    name: entry.name,
    category: entry.name,
    // No difficulty field on templateID 2 — default kept so difficulty-filter
    // UI (where present) stays functional rather than always-empty.
    difficulty: "Beginner",
    duration: "-",
    price: 0,
    seats: 0,
    image: imageForCategory(entry.name),
    shortDescription: "",
    location: "",
  };
}

// Paramotor Trike has no live category row of its own on the backend — it's
// a sub-course under the "Paramotor" category (see courseService.ts's
// SLUG_TO_CATEGORY), so it never appears as a distinct category from the
// live API. Appended manually wherever the category list is rendered
// (home page's "Choose Your Adventure" section and the /courses page) so it
// still gets its own card, per an explicit request.
export const PARAMOTOR_TRIKE_CARD: CourseCategoryCard = {
  id: "paramotor-trike",
  slug: "paramotor-trike",
  name: "Paramotor Trike",
  category: "Paramotor",
  difficulty: "Advanced",
  duration: "-",
  price: 0,
  seats: 0,
  image: "/images/courses/paramotor-trike.jpg",
  shortDescription: "",
  location: "",
};

export function withParamotorTrike(categories: CourseCategoryCard[]): CourseCategoryCard[] {
  return categories.length > 0 ? [...categories, PARAMOTOR_TRIKE_CARD] : categories;
}

export function useCourseCategories(): {
  categories: CourseCategoryCard[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [categories, setCategories] = useState<CourseCategoryCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!isApiConnected()) {
      setCategories([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    listCourseCategoryEntries()
      .then((entries) => setCategories(entries.map(toCard)))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load courses"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { categories, loading, error, refresh: load };
}
